import { Point } from "leaflet"
import type { LatLng, Map as LeafletMap } from "leaflet"
import type { CSSProperties, MouseEvent, ReactElement, ReactNode, RefObject } from "react"
import { createRef, useEffect, useState } from "react"
import { useMap } from "react-leaflet"
import { RectangleShapeOverwrite } from ".."
import { usePrevious } from "../../../hooks"
import type { RectangleOverwriteLayer } from "../../../utils"

import "./leaflet-div-layer.styles.scss"

type DivBounds = {
  width: number
  height: number
  originalZoom: number
}

type ProxyBounds = {
  corner1: LatLng
  corner2: LatLng
}

export interface ILeafletDivLayerProps {
  center: LatLng
  className?: string
  children?: ReactNode
  onClick?: (event: MouseEvent<HTMLDivElement>) => void
  onRef?: (proxy: L.Rectangle) => void
  onResize?: () => void
}

export function LeafletDivLayer(props: ILeafletDivLayerProps): ReactElement {
  const { center, className, children, onClick, onRef, onResize } = props

  const divRef: RefObject<HTMLDivElement> = createRef<HTMLDivElement>()
  const [divElement, setDivElement] = useState<HTMLDivElement>()

  const rectRef: RefObject<RectangleOverwriteLayer> = createRef<RectangleOverwriteLayer>()

  const [divBounds, setDivBounds] = useState<DivBounds>()
  const previousDivBounds: DivBounds | undefined = usePrevious(divBounds)
  const map: LeafletMap = useMap()
  const [resizeObserver, setResizeObserver] = useState<ResizeObserver>()
  const [isMounted, setIsMounted] = useState<boolean>(false)

  const [divStyles, setDivStyles] = useState<CSSProperties>({
    transformOrigin: "0% 0%",
    position: "absolute",
    zIndex: 500
  })

  const [proxyBounds, setProxyBounds] = useState<ProxyBounds>()

  useEffect(() => {
    setIsMounted(true)
    return () => {
      setIsMounted(false)
    }
  }, [])

  useEffect(() => {
    if (divRef.current) {
      setDivElement(divRef.current)
    }
  }, [divRef])

  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    if (divElement && !resizeObserver) {
      const observer: ResizeObserver = new ResizeObserver(handleResize)
      observer.observe(divElement as HTMLDivElement)
      setResizeObserver(observer)
      handleResize()
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
    }
  }, [divElement])

  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    if (onRef && rectRef.current) {
      onRef(rectRef.current)
    }
  }, [rectRef])

  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    if (onResize) onResize()
    applyBoundsToHtml()

    if (rectRef.current) {
      rectRef.current.fireEvent("resize")
    }

    map.on("zoom", applyBoundsToHtml)

    return () => {
      map.off("zoom", applyBoundsToHtml)
    }
  }, [proxyBounds])

  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    setProxyBounds(calculateProxyBounds())
  }, [center, divBounds])

  function handleResize(): void {
    if (!isMounted) return
    setDivBounds(calculateDivBounds())
  }

  function calculateProxyBounds(): ProxyBounds | undefined {
    if (!divBounds) return undefined

    let { width, height } = divBounds

    if (
      proxyBounds !== undefined &&
      previousDivBounds &&
      previousDivBounds.width !== 0 &&
      previousDivBounds.height !== 0
    ) {
      const point1: Point = map.latLngToLayerPoint(proxyBounds.corner1)
      const point2: Point = map.latLngToLayerPoint(proxyBounds.corner2)
      const proxyWidth: number = point2.x - point1.x
      const proxyHeight: number = point2.y - point1.y

      width = (proxyWidth / previousDivBounds.width) * width
      height = (proxyHeight / previousDivBounds.height) * height
    }

    const centerPoint: Point = map.latLngToLayerPoint(center)

    const point1: Point = new Point(centerPoint.x - width / 2, centerPoint.y - height / 2)
    const point2: Point = new Point(centerPoint.x + width / 2, centerPoint.y + height / 2)

    return {
      corner1: map.layerPointToLatLng(point1),
      corner2: map.layerPointToLatLng(point2)
    }
  }

  function calculateDivBounds(): DivBounds | undefined {
    if (!divElement) return undefined

    const width: number = divElement.clientWidth
    const height: number = divElement.clientHeight
    const originalZoom: number = map.getZoom()

    return { width, height, originalZoom }
  }

  function applyBoundsToHtml(): void {
    if (!proxyBounds || !divBounds) return

    const point1: Point = map.latLngToLayerPoint(proxyBounds.corner1)
    const point2: Point = map.latLngToLayerPoint(proxyBounds.corner2)

    const width: number = point2.x - point1.x
    const height: number = point2.y - point1.y

    const scaleX: number = width / divBounds.width
    const scaleY: number = height / divBounds.height

    setDivStyles({
      ...divStyles,
      transform: `translate3d(${point1.x}px, ${point1.y}px, 0px) scale3d(${scaleX}, ${scaleY}, 1)`
    })
  }

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: TODO in a later PR
    <div ref={divRef} style={divStyles} className={`leaflet-div-layer ${className || ""}`} onClick={onClick}>
      {divBounds && proxyBounds && (
        <RectangleShapeOverwrite
          ref={rectRef}
          corner1={proxyBounds.corner1}
          corner2={proxyBounds.corner2}
          pathOptions={{
            className: "lealfet-div-layer-proxy",
            fillOpacity: 0,
            weight: 4,
            stroke: false
          }}
        >
          {children}
        </RectangleShapeOverwrite>
      )}
    </div>
  )
}
